/**
 * AgriSolSec — mega-menu hover behavior.
 * On hover-capable devices, force <details> always-open (panel always rendered)
 * and toggle a `.is-active` class on the <details> to control visibility.
 * This decouples visibility from the <details> open/close mechanism, which
 * doesn't reliably trigger CSS transitions on every toggle.
 */
(function () {
  'use strict';

  var OPEN_DELAY = 0;

  function isHoverCapable() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  function closeOthers(except) {
    document.querySelectorAll('header-menu details.mega-menu.is-active').forEach(function (d) {
      if (d !== except) d.classList.remove('is-active');
    });
  }

  function bind(menu) {
    if (menu.dataset.hoverBound === '1') return;
    menu.dataset.hoverBound = '1';

    var details = menu.querySelector('details.mega-menu');
    if (!details) return;
    var summary = details.querySelector('summary');

    // Force <details> always-open on hover-capable devices so the panel
    // stays in the render tree. Visibility is controlled via .is-active.
    if (isHoverCapable()) {
      details.setAttribute('open', '');
    }

    var openTimer = null;

    function clearTimers() {
      if (openTimer) { clearTimeout(openTimer); openTimer = null; }
    }

    var content = details.querySelector('.mega-menu__content');
    // Panel menus (Tablets / Educational / Lifestyle) anchor under their own
    // pill with right-edge overflow protection, exactly like the simple menus.
    var isSimple = details.classList.contains('mega-menu--simple') || details.classList.contains('mega-menu--panel');

    // Position the pop-up each time it opens (and on resize).
    //  - Full menus span the centered nav width; only the little tail moves to
    //    point at the hovered pill.
    //  - Simple menus are anchored under their own pill, with right-edge
    //    overflow protection that shifts the card left when needed.
    function positionCard() {
      if (!content || !summary) return;

      content.style.removeProperty('--mega-shift');
      content.style.removeProperty('--mega-origin');
      content.style.removeProperty('--mega-tail');

      var summaryRect = summary.getBoundingClientRect();
      var pillCenter = summaryRect.left + summaryRect.width / 2;
      var TAIL_HALF = 7; // half the 14px tail, so it centers on the pill

      if (!isSimple) {
        // The card is centered within the nav and capped at the page width, so
        // its left edge varies. offsetLeft/offsetWidth are layout-based (they
        // ignore the card's reveal transform), giving the true left edge even
        // while the card animates. nav rect (no transform) anchors it to the
        // viewport. Then place the tail under the hovered pill.
        var nav = content.offsetParent || menu.closest('.header__inline-menu');
        if (!nav) return;
        var navRect = nav.getBoundingClientRect();
        var cardLeft = navRect.left + content.offsetLeft;
        var tail = pillCenter - cardLeft - TAIL_HALF;
        tail = Math.max(20, Math.min(tail, content.offsetWidth - 34));
        content.style.setProperty('--mega-tail', Math.round(tail) + 'px');

        // Anchor the card just under the PILL (not the nav's padded bottom), so
        // the gap matches the flush spacing of the simple Shop/Contact menus.
        // The 14px CSS margin-top adds the same small gap they use.
        content.style.top = Math.round(summaryRect.bottom - navRect.top) + 'px';
        return;
      }

      // Simple menu: anchored under the pill; shift left if it would overflow.
      var GUTTER = 16;
      var rect = content.getBoundingClientRect();
      var overflowRight = rect.right - (window.innerWidth - GUTTER);
      if (overflowRight > 0) {
        var maxShiftLeft = summaryRect.left - GUTTER;
        var shift = Math.min(overflowRight, maxShiftLeft);
        if (shift > 0) {
          content.style.setProperty('--mega-shift', '-' + Math.round(shift) + 'px');
          content.style.setProperty('--mega-origin', 'top right');
          content.style.setProperty('--mega-tail', Math.round(28 + shift) + 'px');
        }
      }
    }

    function open(delay) {
      clearTimers();
      openTimer = setTimeout(function () {
        // Cursor may have already left the menu (and the entire header) before
        // this delayed open fires. If so, mouseleave on .header-wrapper will
        // have already run and found nothing to close — activating now would
        // strand the panel open with no way to dismiss it.
        if (!menu.matches(':hover')) return;
        closeOthers(details);
        positionCard();
        details.classList.add('is-active');
      }, delay);
    }

    window.addEventListener('resize', function () {
      if (details.classList.contains('is-active')) positionCard();
    });

    // Entering a mega menu opens it. Closing happens via (1) entering a
    // different mega menu, which calls closeOthers, (2) entering a plain
    // top-level item like Home (see bindPlainItems), or (3) leaving the entire
    // header wrapper (bindHeaderLeave).
    menu.addEventListener('mouseenter', function () {
      if (!isHoverCapable()) return;
      open(OPEN_DELAY);
    });

    if (summary) {
      summary.addEventListener('click', function (e) {
        if (!isHoverCapable()) return;
        e.preventDefault();
        var link = summary.querySelector('a[href]');
        if (link && link.href) {
          window.location.href = link.href;
          return;
        }
        var dataUrl = summary.dataset.linkUrl;
        if (dataUrl && dataUrl !== '' && dataUrl !== '#') {
          window.location.href = dataUrl;
        }
      });
    }
  }

  function bindAll() {
    document.querySelectorAll('header-menu').forEach(bind);
  }

  // Hovering a plain top-level item (e.g. Home, or any non-mega link) closes any
  // open mega panel — the cursor is no longer pointing at the menu that opened
  // it, so it should not stay stranded open.
  function bindPlainItems() {
    document.querySelectorAll('.header__inline-menu > ul > li > a.header__menu-item').forEach(function (link) {
      if (link.dataset.plainHoverBound === '1') return;
      link.dataset.plainHoverBound = '1';
      link.addEventListener('mouseenter', function () {
        if (!isHoverCapable()) return;
        closeOthers(null);
      });
    });
  }

  // Desktop-only cache warming. The panel images stay loading="lazy" in the
  // markup (so mobile — where the mega menu is display:none — never downloads
  // them, and Lighthouse's "defer offscreen images" audit stays happy). Here,
  // only on hover-capable devices and only once the browser is idle after load,
  // we pre-fetch them so the first hover (especially Educational's remote CDN
  // image) reveals instantly instead of popping in.
  var imagesWarmed = false;
  function warmPanelImages() {
    if (imagesWarmed || !isHoverCapable()) return;
    imagesWarmed = true;
    var run = function () {
      document.querySelectorAll('.mega-menu__panel-media img').forEach(function (img) {
        var src = img.currentSrc || img.src;
        if (src) { new Image().src = src; }
      });
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 3000 });
    } else {
      setTimeout(run, 1200);
    }
  }

  function bindHeaderLeave() {
    var header = document.querySelector('.header-wrapper');
    if (!header) return;
    header.addEventListener('mouseleave', function () {
      if (!isHoverCapable()) return;
      closeOthers(null);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      bindAll();
      bindPlainItems();
      bindHeaderLeave();
      warmPanelImages();
    });
  } else {
    bindAll();
    bindPlainItems();
    bindHeaderLeave();
    warmPanelImages();
  }

  document.addEventListener('shopify:section:load', function () {
    bindAll();
    bindPlainItems();
    bindHeaderLeave();
  });
})();
